import { Link, useLocation, useNavigate } from "react-router-dom";
import { currentStore } from "@/mocks/dashboard";

const LOGO_SRC =
  "https://static.readdy.ai/image/14e48b2a3119a3da93cb07955cc0990c/70345e36bb0ad42c4823216a62f4fb9a.png";

interface NavItem {
  path: string;
  label: string;
  icon: string;
  badge?: number;
}

const NAV_SECTIONS: NavItem[][] = [
  [
    { path: "/", label: "الرئيسية", icon: "ri-home-5-line" },
    {
      path: "/notifications",
      label: "الإشعارات",
      icon: "ri-notification-3-line",
      badge: 4,
    },
    { path: "/store", label: "بيانات المحل", icon: "ri-store-2-line" },
    { path: "/menu", label: "المنيو", icon: "ri-restaurant-2-line" },
    { path: "/branches", label: "الفروع", icon: "ri-map-pin-line" },
    {
      path: "/employees",
      label: "الموظفون والصلاحيات",
      icon: "ri-team-line",
    },
  ],
  [
    { path: "/pos", label: "نقطة البيع", icon: "ri-shopping-cart-2-line" },
    { path: "/orders", label: "الطلبات", icon: "ri-shopping-bag-3-line" },
    {
      path: "/invoices",
      label: "الفواتير الضريبية",
      icon: "ri-file-shield-2-line",
    },
    { path: "/reports", label: "التقارير", icon: "ri-bar-chart-box-line" },
    { path: "/financial", label: "المعاملات المالية", icon: "ri-wallet-3-line" },
  ],
  [
    { path: "/settings", label: "إعدادات الحساب", icon: "ri-settings-4-line" },
  ],
];

interface SidebarProps {
  onClose?: () => void;
}

export default function Sidebar({ onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <aside className="h-full w-full flex flex-col bg-foreground-950 text-background-100">
      <div className="flex items-center gap-3 px-4 pt-5 pb-4">
        <div className="flex-1 w-full flex items-center justify-center rounded-lg bg-white px-2.5 py-2">
          <img
            src={LOGO_SRC}
            alt="يور كاشير"
            className="h-11 w-auto object-contain"
          />
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full text-background-100/70 hover:bg-white/10 hover:text-background-50 transition-colors duration-200 cursor-pointer"
            aria-label="إغلاق القائمة"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        )}
      </div>

      <div className="mx-4 mb-4 rounded-box bg-white/5 p-4">
        <div className="flex items-center gap-3">
          <img
            src={currentStore.logo}
            alt={currentStore.brandName}
            className="w-12 h-12 rounded-full object-cover ring-2 ring-primary-500/60"
          />
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-background-50 truncate">
              {currentStore.brandName}
            </p>
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary-500/20 px-2 py-0.5 text-[11px] font-medium text-primary-300">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-400"></span>
              {currentStore.statusLabel}
            </span>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {NAV_SECTIONS.map((section, index) => (
          <div
            key={index}
            className={
              index > 0
                ? "mt-3 pt-3 border-t border-white/10"
                : undefined
            }
          >
            {section.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => onClose?.()}
                  className={`group relative flex items-center gap-3 rounded-btn px-3 py-2.5 mb-1 text-sm font-medium transition-colors duration-200 border-r-4 ${
                    active
                      ? "border-primary-500 bg-primary-500/15 text-background-50"
                      : "border-transparent text-background-100/80 hover:bg-white/5 hover:text-background-50"
                  }`}
                >
                  <span
                    className={`w-6 h-6 flex items-center justify-center text-lg shrink-0 ${
                      active
                        ? "text-primary-400"
                        : "text-background-100/60 group-hover:text-background-50"
                    }`}
                  >
                    <i className={item.icon}></i>
                  </span>
                  <span className="flex-1 truncate">{item.label}</span>
                  {typeof item.badge === "number" && item.badge > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-accent-500 text-[11px] font-bold text-foreground-950">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-white/10">
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="w-full flex items-center gap-3 rounded-btn px-3 py-2.5 text-sm font-medium text-background-100/60 hover:bg-white/5 hover:text-red-300 transition-colors duration-200 cursor-pointer"
        >
          <span className="w-6 h-6 flex items-center justify-center text-lg">
            <i className="ri-logout-box-r-line"></i>
          </span>
          تسجيل الخروج
        </button>
        <p className="mt-2 px-3 text-[11px] text-background-100/40">
          يور كاشير — الإصدار 1.0.0
        </p>
      </div>
    </aside>
  );
}